import { useUpdateTask } from "@api/task.api";
import { useSortedCloseStages } from "@api/organizationCloseStage.api";
import { useWorkspaceCloses } from "@api/close.api";
import { UserInfo } from "@components/user/UserInfo";
import {
    Task,
    TaskStatus,
    TaskType,
    getEntityLockedTooltip,
    isEntityLockedTaskType,
} from "@common/types/task";
import { pluralize } from "@common/helpers/string";
import { format } from "date-fns";
import { fullDateFormatWithTime } from "@common/helpers/date";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CopyIcon } from "@icons";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { ReactNode, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTerminatingStatusGuard } from "@hooks/useTerminatingStatusGuard";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { TimePicker } from "@design-system/TimePicker/TimePicker";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { TaskPrioritySelect } from "@components/tasks/TaskPrioritySelect/TaskPrioritySelect";
import { TaskStatusSelect } from "@components/tasks/TaskStatusSelect/TaskStatusSelect";
import { TaskGroupSelect } from "@components/drawers/TaskGroupSelect";
import { Picker } from "@design-system/Picker/Picker.lib";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import classNames from "classnames";
import { useScope } from "@state/scopeContext.lib";
import { useDocumentCategoryLabel } from "@api/documentCategory.api";
import { TaskStatusTerminationConfirmationModal } from "../TaskStatusTerminationConfirmationModal";
import { CloseSelectItem } from "../TaskView/Cells/CloseSelectItem";
import styles from "./TaskDetail.module.scss";
import { TaskDetailAssigneeSelect } from "./TaskDetailAssigneeSelect";
import { TaskDetailDocumentsSection } from "./TaskDetailDocumentsSection";
import { TaskDetailRow } from "./TaskDetailRow";
import { TaskDetailTitle } from "./TaskDetailTitle";

interface TaskDetailProps {
    task: Task;
    readonly?: boolean;
    dependencyRows?: ReactNode;
}

const ReadOnlyValue = ({ children }: { children: React.ReactNode }) => (
    <p className={styles.detailValue}>{children}</p>
);

const EMPTY_VALUE = "-";

export const TaskDetail = ({
    task,
    readonly = false,
    dependencyRows,
}: TaskDetailProps) => {
    const updateTask = useUpdateTask(task);
    const updateStatus = useCallback(
        (status: TaskStatus) => {
            if (readonly) {
                return Promise.resolve(task);
            }
            return updateTask.mutateAsync({ status });
        },
        [updateTask, readonly, task],
    );
    const { organizationId } = useScope();
    const sortedCloseStages = useSortedCloseStages();
    const { data: closes = [] } = useWorkspaceCloses();

    const showCloseStage =
        !!organizationId && sortedCloseStages.length > 0 && !!task.closeId;
    const showClose = !!organizationId && closes.length > 0;
    const selectedCloseStage =
        task.closeStageId != null
            ? sortedCloseStages.find((s) => s.id === task.closeStageId)
            : undefined;
    const selectedClose =
        task.closeId != null
            ? closes.find((c) => c.id === task.closeId)
            : undefined;

    const isEntityLockedTask = isEntityLockedTaskType(task.type);
    const entityLockedTooltip = getEntityLockedTooltip(task.type);

    const {
        handleStatusChange,
        pendingStatus,
        activeBlockerCount,
        confirmChange,
        cancelChange,
        showDontAskAgain,
        shouldSkipConfirmation,
        setShouldSkipConfirmation,
    } = useTerminatingStatusGuard(task, updateStatus);

    const documentCategoryLabel = useDocumentCategoryLabel(
        task.type === TaskType.DOCUMENT_RETRIEVAL
            ? task.view?.filters?.documentCategoryIds
            : undefined,
    );

    const { pathname } = useLocation();
    const copyToClipboard = useCopyToClipboard({
        successMessage: "Task URL copied to clipboard",
    });

    const handleCopyLink = useCallback(() => {
        // Keep the current page path so the link also works on pages other
        // than the workspace tasks page (e.g. org-level clients tasks).
        const searchParams = new URLSearchParams({
            taskId: task.id,
            taskWorkspaceId: task.workspaceId,
        });
        const shareableUrl = `${window.location.origin}${pathname}?${searchParams.toString()}`;

        copyToClipboard(shareableUrl);
    }, [copyToClipboard, pathname, task.id, task.workspaceId]);

    return (
        <>
            <div
                className={classNames(styles.container, {
                    [styles.readOnly]: readonly,
                })}
                aria-disabled={readonly || undefined}
            >
                <div className={styles.detailsListContainer}>
                    <TaskDetailRow label="Title">
                        {readonly ? (
                            <ReadOnlyValue>{task.title}</ReadOnlyValue>
                        ) : (
                            <TaskDetailTitle
                                task={task}
                                onRename={updateTask.mutateAsync}
                            />
                        )}
                    </TaskDetailRow>
                    <TaskDetailRow label="Status">
                        <div className={styles.selectDetail}>
                            <TaskStatusSelect
                                value={task.status}
                                onValueChange={handleStatusChange}
                                buttonProps={{
                                    variant: "primary",
                                    block: false,
                                    className: styles.selectTrigger,
                                }}
                            />
                        </div>
                    </TaskDetailRow>
                    <TaskDetailRow label="Assignee">
                        {readonly && task.assigneeId == null ? (
                            <ReadOnlyValue>{EMPTY_VALUE}</ReadOnlyValue>
                        ) : (
                            <div className={styles.selectDetail}>
                                <TaskDetailAssigneeSelect
                                    assigneeId={task.assigneeId}
                                    onValueChange={(assigneeId) =>
                                        updateTask.mutateAsync({ assigneeId })
                                    }
                                    triggerClassName={styles.selectTrigger}
                                />
                            </div>
                        )}
                    </TaskDetailRow>
                    <TaskDetailRow label="Priority">
                        {readonly && task.priority == null ? (
                            <ReadOnlyValue>{EMPTY_VALUE}</ReadOnlyValue>
                        ) : (
                            <div className={styles.selectDetail}>
                                <TaskPrioritySelect
                                    placeholder="Set priority"
                                    value={task.priority}
                                    onValueChange={async (value) => {
                                        await updateTask.mutateAsync({
                                            priority: value,
                                        });
                                    }}
                                    buttonProps={{
                                        variant: "primary",
                                        block: false,
                                        className: styles.selectTrigger,
                                    }}
                                />
                            </div>
                        )}
                    </TaskDetailRow>
                    <TaskDetailRow label="Group">
                        {readonly && task.groupId == null ? (
                            <ReadOnlyValue>{EMPTY_VALUE}</ReadOnlyValue>
                        ) : (
                            <div className={styles.selectDetail}>
                                <TaskGroupSelect
                                    value={task.groupId}
                                    onChange={(id) =>
                                        updateTask.mutateAsync({ groupId: id })
                                    }
                                    buttonProps={{
                                        variant: "primary",
                                        block: false,
                                        className: styles.selectTrigger,
                                    }}
                                />
                            </div>
                        )}
                    </TaskDetailRow>
                    {showCloseStage && (
                        <TaskDetailRow label="Stage">
                            <div className={styles.selectDetail}>
                                <Picker.Select
                                    value={selectedCloseStage?.id ?? null}
                                    onValueChange={(value) =>
                                        updateTask.mutateAsync({
                                            closeStageId: value,
                                        })
                                    }
                                >
                                    <Picker.ButtonTrigger
                                        render={
                                            <Picker.Button
                                                variant="primary"
                                                size="md"
                                                block={false}
                                                className={styles.selectTrigger}
                                            />
                                        }
                                        value={selectedCloseStage?.name}
                                        placeholder="Set stage"
                                    />
                                    <Picker.Popup>
                                        <Picker.List>
                                            <Picker.Item
                                                onSelect={() =>
                                                    updateTask.mutateAsync({
                                                        closeStageId: null,
                                                    })
                                                }
                                            >
                                                No stage
                                            </Picker.Item>
                                            {sortedCloseStages.map((stage) => (
                                                <Picker.Item
                                                    key={stage.id}
                                                    value={stage.id}
                                                >
                                                    {stage.name}
                                                </Picker.Item>
                                            ))}
                                        </Picker.List>
                                    </Picker.Popup>
                                </Picker.Select>
                            </div>
                        </TaskDetailRow>
                    )}
                    {showClose && (
                        <TaskDetailRow label="Service">
                            <div className={styles.selectDetail}>
                                <Picker.Select
                                    value={selectedClose?.id ?? null}
                                    onValueChange={(value) =>
                                        updateTask.mutateAsync({
                                            closeId: value,
                                            ...(!value && {
                                                closeStageId: null,
                                            }),
                                        })
                                    }
                                >
                                    <Picker.ButtonTrigger
                                        render={
                                            <Picker.Button
                                                variant="primary"
                                                size="md"
                                                block={false}
                                                className={styles.selectTrigger}
                                            />
                                        }
                                        value={selectedClose?.name}
                                        placeholder="Set service"
                                    />
                                    <Picker.Popup>
                                        <Picker.List>
                                            <Picker.Item
                                                onSelect={() =>
                                                    updateTask.mutateAsync({
                                                        closeId: null,
                                                        closeStageId: null,
                                                    })
                                                }
                                            >
                                                No service
                                            </Picker.Item>
                                            {closes.map((close) => (
                                                <CloseSelectItem
                                                    key={close.id}
                                                    id={close.id}
                                                    name={close.name}
                                                    entityId={close.entityId}
                                                    startDate={close.startDate}
                                                    endDate={close.endDate}
                                                />
                                            ))}
                                        </Picker.List>
                                    </Picker.Popup>
                                </Picker.Select>
                            </div>
                        </TaskDetailRow>
                    )}
                    <TaskDetailRow label="Due date">
                        {readonly && !task.dueDate ? (
                            <ReadOnlyValue>{EMPTY_VALUE}</ReadOnlyValue>
                        ) : (
                            <div className={styles.datePickerContainer}>
                                <DatePicker
                                    mode="day"
                                    variant="panel-input"
                                    clearable
                                    showTriggerIcon={false}
                                    placeholder="Set due date"
                                    value={task.dueDate ?? undefined}
                                    onValueChange={(value) =>
                                        updateTask.mutateAsync({
                                            dueDate: value ?? null,
                                        })
                                    }
                                />
                            </div>
                        )}
                    </TaskDetailRow>
                    <TaskDetailRow label="Estimate">
                        {readonly && !task.estimatedTime ? (
                            <ReadOnlyValue>{EMPTY_VALUE}</ReadOnlyValue>
                        ) : (
                            <div className={styles.datePickerContainer}>
                                <TimePicker
                                    value={task.estimatedTime ?? undefined}
                                    disabled={readonly}
                                    hideIcon
                                    onChange={(value) =>
                                        updateTask.mutateAsync({
                                            estimatedTime: value ?? null,
                                        })
                                    }
                                />
                            </div>
                        )}
                    </TaskDetailRow>
                    <TaskDetailRow
                        label={pluralize("Entity", task.entityIds?.length ?? 0)}
                    >
                        {readonly &&
                        (task.entityIds == null ||
                            task.entityIds.length === 0) ? (
                            <ReadOnlyValue>{EMPTY_VALUE}</ReadOnlyValue>
                        ) : (
                            <div
                                className={classNames(
                                    styles.selectDetail,
                                    styles.entityMultiSelectTrigger,
                                )}
                            >
                                <EntityFilterCombobox
                                    mode="selection"
                                    disabled={isEntityLockedTask}
                                    renderTrigger={({
                                        triggerProps,
                                        selectedEntities,
                                    }) => (
                                        <Tooltip
                                            content={
                                                isEntityLockedTask
                                                    ? entityLockedTooltip
                                                    : undefined
                                            }
                                        >
                                            <Button
                                                {...triggerProps}
                                                type="button"
                                                variant="primary"
                                                size="md"
                                                className={
                                                    styles.entityMultiSelectInput
                                                }
                                            >
                                                {selectedEntities.length > 0 ? (
                                                    <EntityAvatarGroup
                                                        entities={
                                                            selectedEntities
                                                        }
                                                        max={3}
                                                        size="xs"
                                                    />
                                                ) : (
                                                    "Add entity"
                                                )}
                                            </Button>
                                        </Tooltip>
                                    )}
                                    filters={{
                                        entityIds: task.entityIds ?? [],
                                    }}
                                    onChange={({ entityIds }) => {
                                        updateTask.mutateAsync({
                                            entityIds: entityIds ?? [],
                                        });
                                    }}
                                />
                            </div>
                        )}
                    </TaskDetailRow>
                    {dependencyRows}
                    <TaskDetailRow label="Created by">
                        <div className={styles.detailValue}>
                            {task.createdBy ? (
                                <UserInfo user={task.createdBy} size="sm" />
                            ) : (
                                <>
                                    <KickAvatar size="sm" aria-hidden />
                                    <span className="text-gray-900 ml-2">
                                        Kick
                                    </span>
                                </>
                            )}
                        </div>
                    </TaskDetailRow>
                    <TaskDetailRow label="Created on">
                        <p className={styles.detailValue}>
                            {format(
                                new Date(task.createdAt),
                                fullDateFormatWithTime,
                            )}
                        </p>
                    </TaskDetailRow>
                    <TaskDetailRow label="Updated on">
                        <p className={styles.detailValue}>
                            {format(
                                new Date(task.updatedAt),
                                fullDateFormatWithTime,
                            )}
                        </p>
                    </TaskDetailRow>
                    {!readonly && (
                        <TaskDetailRow label="Task URL">
                            <div className={styles.detailValue}>
                                <Tooltip content="Copy task URL">
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        iconOnly
                                        aria-label="Copy task URL"
                                        onClick={handleCopyLink}
                                    >
                                        <CopyIcon />
                                    </Button>
                                </Tooltip>
                            </div>
                        </TaskDetailRow>
                    )}
                    {documentCategoryLabel && (
                        <TaskDetailRow label="Document category">
                            <p className={styles.detailValue}>
                                {documentCategoryLabel}
                            </p>
                        </TaskDetailRow>
                    )}
                    <TaskDetailRow label="Attachments">
                        <TaskDetailDocumentsSection
                            task={task}
                            readonly={readonly}
                        />
                    </TaskDetailRow>
                </div>
            </div>
            <TaskStatusTerminationConfirmationModal
                pendingStatus={pendingStatus}
                activeBlockerCount={activeBlockerCount}
                onConfirm={confirmChange}
                onCancel={cancelChange}
                showDontAskAgain={showDontAskAgain}
                shouldSkipConfirmation={shouldSkipConfirmation}
                onShouldSkipConfirmationChange={setShouldSkipConfirmation}
            />
        </>
    );
};
